<script>
import Header from "@/components/Table/Header.vue";
import DataTable from "@/components/Table/DataTable.vue";
import ActionsButton from "@/components/Table/ActionsButton.vue";
import TablePagination from "@/components/Table/TablePagination.vue";
import {mapGetters} from "vuex";
import moment from "moment";

export default {
  name: "GeneratesList",
  components: {TablePagination, ActionsButton, DataTable, Header},

  data() {
    return {
      breadcrumbs: [
        {text: "Home",    path: "/dashboard", isActive: false},
        {text: "Billing", path: "",           isActive: false},
        {text: "Generate List", path: "/generates", isActive: true},
      ],
      month: null,
      search: null,
      pagination: {
        current_page: 1,
        per_page: 15,
        total: 0,
        last_page: 0,
        from: 0,
      },
    };
  },

  computed: {
    ...mapGetters("Reading", ["generates", "generatesMeta"]),
  },

  watch: {
    generatesMeta(val) {
      if (val) {
        this.pagination = {
          current_page: val.current_page,
          per_page:     parseInt(val.per_page),
          total:        val.total,
          last_page:    val.last_page,
          from:         val.from || 0,
        };
      }
    },
  },

  created() {
    this.$store.dispatch("Config/setBreadCrumb", this.breadcrumbs);
  },

  mounted() {
    const q = this.$route.query;
    this.month  = q.month  || null;
    this.search = q.search || null;
    this.pagination.current_page = q.page     ? parseInt(q.page)     : 1;
    this.pagination.per_page     = q.per_page ? parseInt(q.per_page) : 15;
    this.fetchList();
  },

  methods: {
    fetchList() {
      const params = {
        page:     this.pagination.current_page,
        per_page: this.pagination.per_page,
        month:    this.month   || undefined,
        search:   this.search  || undefined,
      };
      this.$store.dispatch("Reading/getGenerates", params);
      this.$router.replace({query: params}).catch(() => {});
    },

    onMonthChange() {
      this.pagination.current_page = 1;
      this.fetchList();
    },

    clearMonth() {
      this.month = null;
      this.pagination.current_page = 1;
      this.fetchList();
    },

    onPageChange(page) {
      this.pagination.current_page = page;
      this.fetchList();
    },

    onPageSizeChanged(size) {
      this.pagination.per_page     = size;
      this.pagination.current_page = 1;
      this.fetchList();
    },

    formatMonth(year, month) {
      return moment(`${year}-${String(month).padStart(2, '0')}`).format('MMMM YYYY');
    },

    typeBadge(type) {
      return type === 'bill' ? 'badge-success' : 'badge-primary';
    },

    statusBadge(status) {
      const map = {completed: 'badge-success', processing: 'badge-warning', pending: 'badge-secondary'};
      return map[status] || 'badge-secondary';
    },

    confirmDelete(generate) {
      const label = `${this.formatMonth(generate.generate_year, generate.generate_month)} — ${generate.type}`;
      this.$confirm({
        message: `Are you sure you want to delete "${label}"? This will permanently delete all related ${generate.type} data.`,
        button:  {no: "Cancel", yes: "Yes, Delete"},
        callback: (confirmed) => {
          if (!confirmed) return;
          this.$store.dispatch("Reading/deleteGenerate", generate.id)
            .then(() => {
              this.$toastr.s(`"${label}" deleted successfully.`, "Deleted");
            })
            .catch(() => {
              this.$toastr.e("Something went wrong. Please try again.", "Error");
            });
        },
      });
    },
  },
};
</script>

<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <h6 class="text-uppercase font-weight-bold text-dark">Generate List</h6>
      </div>

      <div class="col-md-12">
        <div class="card p-4">

          <!-- Filter bar -->
          <div class="row mb-3 align-items-center">
            <div class="col-md-3">
              <el-date-picker
                  v-model="month"
                  type="month"
                  placeholder="Filter by month"
                  format="MM/yyyy"
                  value-format="yyyy-MM"
                  size="small"
                  style="width:100%"
                  @change="onMonthChange"
                  clearable
              />
            </div>
            <div class="col-md-2" v-if="month">
              <el-button size="small" @click="clearMonth">Clear</el-button>
            </div>
          </div>

          <div class="row">
            <div class="col-md-12">
              <DataTable>
                <template #header>
                  <Header :sort="false" title="SL"/>
                  <Header :sort="false" title="Month"/>
                  <Header :sort="false" title="Type"/>
                  <Header :sort="false" title="Status"/>
                  <Header :sort="false" title="Total"/>
                  <Header :sort="false" title="Generated"/>
                  <Header :sort="false" title="Not Generated"/>
                  <Header :sort="false" title="Created By"/>
                  <Header :sort="false" title="Created At"/>
                  <Header :sort="false" title="Actions"/>
                </template>

                <tr v-for="(item, index) in generates" :key="item.id">
                  <td>{{ pagination.from + index }}</td>
                  <td class="font-weight-bold">
                    {{ formatMonth(item.generate_year, item.generate_month) }}
                  </td>
                  <td>
                    <span class="badge" :class="typeBadge(item.type)">{{ item.type }}</span>
                  </td>
                  <td>
                    <span class="badge" :class="statusBadge(item.status)">{{ item.status }}</span>
                  </td>
                  <td>{{ item.total_count }}</td>
                  <td>{{ item.generated_count }}</td>
                  <td>{{ item.not_generated_count }}</td>
                  <td>
                    {{ item.created_by
                        ? item.created_by.first_name + ' ' + item.created_by.last_name
                        : '—' }}
                  </td>
                  <td>{{ item.created_at | dateTimeFormat }}</td>
                  <td>
                    <ActionsButton>
                      <router-link
                          :to="{
                            name: 'GeneratesDetail',
                            params: { id: item.id },
                            query: {
                              type:  item.type,
                              year:  item.generate_year,
                              month: item.generate_month
                            }
                          }"
                      >
                        Details
                      </router-link>

                      <a
                          v-if="checkUsrPermissions(['Reading_all_delete', 'Bill_all_delete'])"
                          href="#"
                          class="text-danger"
                          @click.prevent="confirmDelete(item)"
                      >
                        Delete
                      </a>
                    </ActionsButton>
                  </td>
                </tr>

                <tr v-if="!generates.length">
                  <td colspan="10" class="text-center font-weight-bold py-4">
                    No data found
                  </td>
                </tr>
              </DataTable>

              <div class="mt-4" v-if="generates.length">
                <TablePagination
                    :pagination="pagination"
                    @pagechanged="onPageChange"
                    @pagesizechanged="onPageSizeChanged"
                />
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
}
.badge-primary  { background: #321fdb; }
.badge-success  { background: #2eb85c; }
.badge-warning  { background: #f9b115; color: #333; }
.badge-secondary{ background: #9da5b1; }
</style>
